'use client';

import {
  ApiDatasetPipelinesListStatusEnum,
  ApiDatasetPipelinesListTaskTypeEnum,
  DataPipelineCount,
  DatasetPipelineList,
  DatasetPipelineStatus,
} from '@api';
import { FormStep } from '@components/form';
import { DataTableColumnHeader } from '@components/table';
import { DataTableFacetedFilter } from '@components/table/DataTableFacetedFilter/DataTableFacetedFilter';
import { Tooltip } from '@components/ui';
import { RadioElement } from '@components/ui/RadioButton/RadioButton';
import { useApi } from '@data-access';
import { useTranslation, NAMESPACES } from '@i18n';
import { getDatasetColumns } from '@sections/DatasetInventoryTable/_components/Columns/Columns';
import columnStyles from '@sections/DatasetInventoryTable/_components/Columns/Columns.module.scss';
import { DatasetInventoryTable } from '@sections/DatasetInventoryTable/DatasetInventoryTable';
import { datasetInventoryCounterStore, questionnaireStore } from '@stores';
import { ColumnDef } from '@tanstack/react-table';
import { AppRoutes } from '@types';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';

import styles from './DatasetSelectionStep.module.scss';

const NON_CONCIERGE_TASK_TYPES = Object.values(ApiDatasetPipelinesListTaskTypeEnum).filter(
  taskType => taskType !== ApiDatasetPipelinesListTaskTypeEnum.concierge,
);

interface DatasetSelectionStepProps {
  onBack: () => void;
  onNext: () => void;
}

export const DatasetSelectionStep = observer(({ onBack, onNext }: DatasetSelectionStepProps) => {
  const { t } = useTranslation(NAMESPACES.DATASET);
  const { questionnaireData } = questionnaireStore;
  const { datasetPiplineApi } = useApi();

  const [datasets, setDatasets] = useState<DatasetPipelineList[] | null>(null);
  const [counts, setCounts] = useState<DataPipelineCount | undefined>(undefined);
  const [selectedPipeline, setSelectedPipeline] = useState<DatasetPipelineList | null>(null);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [viewOptionsSlot, setViewOptionsSlot] = useState<HTMLElement | null>(null);

  /** Extra API filters to be added to the API request (assessment step) - completed status, pre-filtered to the model when entering via "Assess model" */
  const extraApiFilters = useMemo(
    () => ({
      status: ApiDatasetPipelinesListStatusEnum.completed,
      taskType: NON_CONCIERGE_TASK_TYPES,
      ...(questionnaireData?.isModelLocked && { aiModelName: [questionnaireData.modelName] }),
    }),
    [questionnaireData?.isModelLocked, questionnaireData?.modelName],
  );

  const handleNext = async () => {
    if (!selectedPipeline) return;

    setIsSavingSelection(true);
    try {
      const fetchedPipeline = await datasetPiplineApi.apiDatasetPipelinesRetrieve({ id: selectedPipeline.id });

      if (
        fetchedPipeline.status !== DatasetPipelineStatus.completed ||
        fetchedPipeline.dataset?.hasPredictions !== true
      ) {
        return;
      }

      const aiModel = fetchedPipeline.aiModel ?? selectedPipeline.aiModel;
      const resolvedTaskType = fetchedPipeline.taskType ?? selectedPipeline.taskType ?? aiModel?.taskType;

      // Questionnaire answers are scoped to the selected dataset pipeline, so a change of
      // pipeline must clear the previously loaded answers/metrics and let the test step refetch.
      if (questionnaireData?.dataPipeline?.id !== fetchedPipeline.id) {
        questionnaireStore.resetQuestionnaireAnswersAndSelectedMetrics();
      }

      questionnaireStore.addQuestionnaireData({
        dataPipeline: {
          id: fetchedPipeline.id,
          aiModelId: aiModel?.id,
          pipelineName: selectedPipeline.taskName,
          dataset: fetchedPipeline.dataset,
          perturbedDatasets: fetchedPipeline.perturbedDatasets,
        },
        ...(aiModel && {
          modelId: aiModel.id,
          modelName: aiModel.name,
          useCaseId: aiModel.useCase?.id,
          useCaseName: aiModel.useCase?.name,
        }),
        ...(resolvedTaskType ? { taskType: resolvedTaskType } : {}),
      });
      onNext();
    } finally {
      setIsSavingSelection(false);
    }
  };

  const modelNameList = counts
    ? Object.entries(counts.modelNameCount).map(([value, count]) => ({ value, count }))
    : undefined;

  const columns = useMemo<ColumnDef<DatasetPipelineList>[]>(() => {
    const inventoryColumns = getDatasetColumns(counts, true);
    const selectCol: ColumnDef<DatasetPipelineList> = {
      id: 'select',
      size: 48,
      minSize: 48,
      header: () => null,
      cell: ({ row }) => {
        const isDisabled =
          row.original.status !== DatasetPipelineStatus.completed || row.original.dataset?.hasPredictions !== true;
        const radio = (
          <RadioElement
            option={{ id: row.id, name: '', disabled: isDisabled }}
            isChecked={row.getIsSelected()}
            onChange={() => row.toggleSelected()}
            radioOnly
          />
        );

        if (!isDisabled) {
          return radio;
        }

        return (
          <Tooltip
            content={t('dataset:selectionStep.disabledTooltip')}
            placement="top"
            className={styles.disabledAvailabilityTooltip}
            isFixed
          >
            <div className={styles.disabledSelect}>{radio}</div>
          </Tooltip>
        );
      },
      enableSorting: false,
      enableHiding: false,
    };

    // Model is locked (not user-changeable) once entering pre-filtered to a model - the facet would only ever show that one model
    const isModelLocked = !!questionnaireData?.isModelLocked;

    const aiModelCol: ColumnDef<DatasetPipelineList> = {
      id: 'aiModelName',
      accessorFn: row => row.aiModel?.name,
      header: ({ column, table }) =>
        isModelLocked ? (
          <DataTableColumnHeader
            column={column}
            title="Model"
          />
        ) : (
          <DataTableFacetedFilter
            column={column}
            title="Model"
            options={(modelNameList ?? []).map(({ value }) => ({ label: <div>{value}</div>, value }))}
            list={modelNameList}
            store={datasetInventoryCounterStore}
            setLoader={(val: boolean) => datasetInventoryCounterStore.setLoader(val)}
            hideSelectedBadges
            hasActiveFilters={table.getState().columnFilters?.length > 0}
          />
        ),
      cell: ({ row }) =>
        row.original.aiModel ? (
          <Link
            href={`${AppRoutes.modelDetails}/${row.original.aiModel.id}`}
            className={clsx(columnStyles.row, columnStyles.underline)}
          >
            {row.original.aiModel.name}
          </Link>
        ) : (
          <span className={columnStyles.row}>-</span>
        ),
      enableSorting: false,
      enableColumnFilter: !isModelLocked,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    };

    const filtered = inventoryColumns
      .filter(col => (col as any).id !== 'actions' && (col as any).id !== 'aiModelName')
      .map(col => {
        // Status is always "completed" here (extraApiFilters) - the filter is never meaningful in this step.
        if ((col as any).accessorKey === 'status') {
          return {
            ...(col as object),
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="Status"
              />
            ),
            enableColumnFilter: false,
          } as ColumnDef<DatasetPipelineList>;
        }
        // Task type is only locked alongside the model - when unscoped, it's still a useful way to narrow datasets.
        if ((col as any).accessorKey === 'taskType' && isModelLocked) {
          return {
            ...(col as object),
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="Model task type"
              />
            ),
            enableColumnFilter: false,
          } as ColumnDef<DatasetPipelineList>;
        }
        if ((col as any).accessorKey !== 'taskName') return col as ColumnDef<DatasetPipelineList>;
        return {
          ...(col as object),
          cell: ({ row }) => (
            <Link
              href={`${AppRoutes.datasetInventory}/${row.original.id}/`}
              className={clsx(columnStyles.fontWeight500, columnStyles.underline)}
            >
              {row.original.taskName || '-'}
            </Link>
          ),
        } as ColumnDef<DatasetPipelineList>;
      });

    // filtered: [taskName, status, taskType, startTime, endTime, predictions, perturbations, errorMessage]
    // desired order: select | name | status | model | taskType | rest
    const [nameCol, statusCol, taskTypeCol, ...rest] = filtered;
    return [selectCol, nameCol, statusCol, aiModelCol, taskTypeCol, ...rest];
  }, [t, counts, modelNameList, questionnaireData?.isModelLocked]);

  const emptyContent = (
    <div className={styles.empty}>
      {questionnaireData?.isModelLocked && (
        <p>
          {t('dataset:selectionStep.noPipelinesFound')} <strong>{questionnaireData.modelName}</strong>.
        </p>
      )}
      <p>
        <Link
          href={AppRoutes.datasetInventoryAdd}
          className={styles.link}
        >
          {t('dataset:selectionStep.addDataset')}
        </Link>{' '}
        to get started.
      </p>
    </div>
  );

  return (
    <div className={styles.section}>
      <FormStep
        title={t('dataset:selectionStep.title')}
        totalSteps={3}
        onBack={onBack}
        onNext={handleNext}
        disabledNext={!selectedPipeline || isSavingSelection}
        nextText="Continue"
        hideSteps
        hideModelAndUsecase
      >
        <div className={styles.container}>
          <div className={styles.hintRow}>
            <p className={styles.hint}>
              <span className={styles.hintText}>Click a pipeline name to view its details.</span>{' '}
              <span className={styles.hint}>{t('dataset:selectionStep.hint')}</span>
            </p>
            <div ref={setViewOptionsSlot} />
          </div>
          {questionnaireData?.isModelLocked && (
            <span className={styles.modelScopeNote}>
              {t('dataset:selectionStep.modelScopeNote')}{' '}
              <span className={styles.modelScopeNoteText}>{questionnaireData.modelName}</span>
            </span>
          )}

          <DatasetInventoryTable
            columns={columns}
            data={datasets}
            setData={setDatasets}
            setCounts={setCounts}
            viewOptionsContainer={viewOptionsSlot}
            onRowSelect={row =>
              setSelectedPipeline(
                row?.status === DatasetPipelineStatus.completed && row?.dataset?.hasPredictions === true ? row : null,
              )
            }
            extraApiFilters={extraApiFilters}
            defaultSelectedId={
              questionnaireData?.dataPipeline?.dataset?.hasPredictions ? questionnaireData?.dataPipeline?.id : null
            }
            emptyContent={emptyContent}
            disableQuerySync
            hideFilterPersistence
            getRowClassName={row => {
              const isDisabled = row.status !== DatasetPipelineStatus.completed || row.dataset?.hasPredictions !== true;
              return isDisabled ? styles.disabledRow : undefined;
            }}
          />
        </div>
      </FormStep>
    </div>
  );
});
